import {
  Box,
  Flex,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  useReactTable,
  PaginationTableState,
} from '@tanstack/react-table';
import React, { useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { Loan } from '@finley-technologies/finley-types';

import {
  SummarizedLoanFragmentGQLType,
  BaseOrganizationFragmentGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { truncateUuid } from 'packages/common-frontend/utils';
import { LoanStatusBadge } from 'packages/funding-requests/ui/shared/StatusBadge';
import { colors } from '~/packages/common-frontend/colors';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { SearchBarInput } from '~/packages/common-frontend/components/SearchAndCopyComponents';
import { TanStackPaginationControl } from '~/packages/common-frontend/components/TanStack';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { getColumnNames } from '~/packages/common-frontend/hooks/useTableColumnPreferences';
import { tableHeaderStyle } from '~/packages/common-frontend/styles';
import { TableSkeleton } from '~/packages/loans/ui/TableSkeleton';

const TABLE_COLUMN_CONFIGS: ColumnDef<SummarizedLoanFragmentGQLType, any>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Loan Name',
    cell: function formatLoanName({ getValue }) {
      return <Typography variant="base-muted">{getValue() || '-'}</Typography>;
    },
  },
  {
    id: 'loanId',
    accessorKey: 'id',
    header: 'Loan ID',
    cell: function formatLoanId({ getValue }) {
      return getValue() ? (
        <Link as={RouterLink} to={`/loans/${getValue()}`}>
          <Typography
            colorScheme="blue"
            fontWeight="medium"
            variant="base-muted"
          >
            {truncateUuid(getValue())}
          </Typography>
        </Link>
      ) : (
        '-'
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'calculatedLoanStatus',
    cell: function renderLoanStatus({ getValue }) {
      return (
        <Flex>
          <LoanStatusBadge status={getValue()} />
        </Flex>
      );
    },
  },
  {
    id: 'creditFacilityName',
    accessorKey: 'creditFacilityName',
    header: 'Facility',
  },
  {
    id: 'externalFacilityId',
    header: 'External Facility ID',
    accessorKey: 'externalFacilityId',
    cell: function renderExternalFacilityId({ getValue }) {
      return getValue() ?? '-';
    },
  },
  {
    id: 'externalLoanId',
    header: 'External Loan ID',
    accessorKey: 'externalLoanId',
    cell: function renderExternalLoanId({ getValue }) {
      return getValue() ?? '-';
    },
  },
  {
    id: 'fundedDate',
    header: 'Funded Date',
    accessorKey: 'originationDate',
    cell: function renderFundedDate({ getValue }) {
      return getValue()
        ? commonDate.fullDateStrToHumanDateString(getValue())
        : '-';
    },
  },
  {
    id: 'nextPaymentDate',
    header: 'Next Payment Date',
    accessorKey: 'nextPaymentDate',
    cell: function renderNextPaymentDate({ getValue }) {
      return getValue()
        ? commonDate.fullDateStrToHumanDateString(getValue())
        : '-';
    },
  },
  {
    id: 'maturityDate',
    header: 'Maturity Date',
    accessorKey: 'maturityDate',
    cell: function renderMaturityDate({ getValue }) {
      return getValue()
        ? commonDate.fullDateStrToHumanDateString(getValue())
        : '-';
    },
  },
  {
    id: 'borrower',
    header: 'Borrower',
    accessorFn: (row) => row.borrowerOrganization.name,
  },
  {
    id: 'lenders',
    header: 'Lenders',
    accessorKey: 'lenderOrganizations',
    cell: function formatLenders({ getValue }) {
      return (
        <Flex direction="column" gap="0.5rem">
          {getValue()
            .slice(0, 1)
            .map((lender: BaseOrganizationFragmentGQLType) => {
              return <Flex key={lender.id}>{lender.name}</Flex>;
            })}

          {getValue().length > 1 && (
            <Tooltip
              sx={{
                placement: 'left',
                bg: 'transparent',
                boxShadow: 'none',
              }}
              label={
                <Box
                  sx={{
                    p: '1rem',
                    rounded: 'lg',
                    backgroundColor: colors.aurora[500],
                  }}
                >
                  {getValue()
                    .slice(1)
                    .map((lender: BaseOrganizationFragmentGQLType) => (
                      <Flex key={lender.id}>{lender.name}</Flex>
                    ))}
                </Box>
              }
            >
              <Flex>
                <span style={{ borderBottom: '1px dotted gray' }}>
                  Other Lenders
                </span>
              </Flex>
            </Tooltip>
          )}
          {getValue().length === 0 && '-'}
        </Flex>
      );
    },
  },
];

const HEADERS = Object.values(getColumnNames(TABLE_COLUMN_CONFIGS));

const LOAN_STATUS_ORDER: readonly Loan.CalculatedLoanStatus[] = [
  'overdue',
  'pending',
  'initiated',
  'funded',
  'canceled',
];
const loanStatusIndexMap = LOAN_STATUS_ORDER.reduce<Record<string, number>>(
  (acm, status, idx) => ({ ...acm, [status]: idx }),
  {},
);

const isMatch = (search: string, input?: string | null): boolean => {
  if (!input) return false;
  return input.toLowerCase().includes(search);
};

type LoansTableProps = {
  initialTanstackTableState?: Partial<PaginationTableState>;
  loans: SummarizedLoanFragmentGQLType[];
  loading: boolean;
};
export const LoansTable = ({
  initialTanstackTableState = {
    pagination: {
      pageSize: 50,
      pageIndex: 0,
    },
  },
  loans,
  loading,
}: LoansTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const sortedLoans: SummarizedLoanFragmentGQLType[] = useMemo(() => {
    return loans.slice().sort((a, b) => {
      return (
        loanStatusIndexMap[a.calculatedLoanStatus] -
        loanStatusIndexMap[b.calculatedLoanStatus]
      );
    });
  }, [loans]);

  const filteredEntries: SummarizedLoanFragmentGQLType[] = useMemo(() => {
    if (!searchQuery) {
      return sortedLoans;
    }

    const search = searchQuery.toLowerCase();

    // No need to support every attribute on the loan, so can just
    // single them out as needed and ignore the others.
    // Can add to this list as we deem it'll be helpful for now
    return sortedLoans.filter((loan) => {
      if (isMatch(search, loan.name)) return true;
      if (isMatch(search, loan.id)) return true;
      if (isMatch(search, loan.calculatedLoanStatus)) return true;
      if (isMatch(search, loan.creditFacilityName)) return true;
      if (isMatch(search, loan.borrowerOrganization.name)) return true;
      for (const lender of loan.lenderOrganizations) {
        if (isMatch(search, lender.name)) return true;
      }
      if (isMatch(search, loan.externalLoanId)) return true;
      if (isMatch(search, loan.externalFacilityId)) return true;

      return false;
    });
  }, [searchQuery, sortedLoans]);

  const table = useReactTable({
    data: filteredEntries,
    columns: TABLE_COLUMN_CONFIGS,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: initialTanstackTableState,
  });
  const tableRef = useRef<HTMLDivElement>(null);

  // Don't memoize to avoid timing issues with table state initialization
  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  if (loading) {
    return <TableSkeleton headers={HEADERS} />;
  }

  return (
    <>
      <Flex
        id="loans-table-search-bar"
        sx={{
          gap: '1rem',
          justifyContent: 'center',
          py: '1.5rem',
        }}
        ref={tableRef}
      >
        <SearchBarInput query={searchQuery} onChangeQuery={setSearchQuery} />
        <CsvXlsxDownloadSelect
          dataRows={csvDownload.dataRows}
          headers={csvDownload.headers}
          filenamePrefix="transaction-line-items"
        />
      </Flex>
      <TableContainer id="loans-table-container">
        <Table sx={{ borderColor: colors.gray[100], borderWidth: '1px' }}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id} sx={tableHeaderStyle}>
                    {flexRender(
                      String(header.column.columnDef.header),
                      header.getContext(),
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    sx={{
                      backgroundColor: 'white',
                      px: '1rem',
                      color: colors.black[70],
                      minWidth: '12rem',
                      maxWidth: cell.column.columnDef.meta?.maxWidth || 'auto',
                      whiteSpace: 'normal',
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <TanStackPaginationControl table={table} />
      {filteredEntries.length === 0 && (
        <Box
          backgroundColor={colors.gray[20]}
          minH="4rem"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text textAlign="center">No entries found.</Text>
        </Box>
      )}
    </>
  );
};
